import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import statusConfig from '@/../config/status.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import BasePage from '@/components/layout/BasePage';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import Items from '@/features/Quote/components/Items';
import ContactDetailsDuringLoad from '@/features/Quote/components/modals/ContactDetailsDuringLoad';
import KvK from '@/features/Quote/components/modals/KvK';
import PremiumPercentage from '@/features/Quote/components/modals/PremiumPercentage';
import SectorCode from '@/features/Quote/components/modals/SectorCode';
import SlstCoverage from '@/features/Quote/components/modals/SlstCoverage';
import WageInfo from '@/features/Quote/components/modals/WageInfo';
import Sidebar from '@/features/Quote/components/sidebar/Sidebar';
import { useAppStatePolling } from '@/hooks/useAppStatePolling';
import { useMediaQuery, XL_QUERY } from '@/hooks/useMediaQuery';
import { isFeatureActive } from '@/lib/featureFlags';
import { notifyParent } from '@/services/parentMessenger';
import { getCurrentPremiumRatePercentage } from '@/services/utils';
import { FeatureFlag } from '@/types/app';

// Helper to get localStorage key for contact details dialog (outside component to avoid dependency issues)
const getContactDetailsShownKey = (policyId: string) => `quotr_contact_details_shown_${policyId}`;

const Quote = (): React.JSX.Element => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const posthog = usePostHog();
	const { id } = useParams<{ id: string }>();
	const { appData, isError, config, isMobileSidebarOpen, setIsMobileSidebarOpen } = useAppContext();
	const [kvkModalOpen, setKvkModalOpen] = useState(false);
	const [sectorCodeModalOpen, setSectorCodeModalOpen] = useState(false);
	const [wageInfoModalOpen, setWageInfoModalOpen] = useState(false);
	const [slstCoverageModalOpen, setSlstCoverageModalOpen] = useState(false);
	const [premiumPercentageModalOpen, setPremiumPercentageModalOpen] = useState(false);
	const [contactDetailsModalOpen, setContactDetailsModalOpen] = useState(false);
	const premiumNudgeDoneRef = useRef(false);
	const quotesViewedNotifiedRef = useRef(false);
	const currentPremiumRate = getCurrentPremiumRatePercentage(appData);
	const [appStatePollingKey, setAppStatePollingKey] = useState(0);
	useAppStatePolling({
		id,
		enabled: !!id,
		key: appStatePollingKey,
		streamQuotesProductType: 'sick_leave_short_term'
	});

	const restartPolling = useCallback(() => {
		setAppStatePollingKey((prev) => prev + 1);
	}, []);

	// The mobile drawer's `SheetContent` is hidden via `xl:hidden`, but the Sheet
	// itself stays logically open — its overlay (and Radix's focus trap) would
	// otherwise persist over the desktop layout if the viewport grows past `xl`
	// while the drawer is open (e.g. rotating a tablet, resizing the window).
	const isDesktopLayout = useMediaQuery(XL_QUERY);
	useEffect(() => {
		if (isDesktopLayout) setIsMobileSidebarOpen(false);
	}, [isDesktopLayout, setIsMobileSidebarOpen]);

	// `isMobileSidebarOpen` lives in AppContext, which wraps every route, so it
	// wouldn't reset on its own when navigating away from this page. Close it on
	// unmount so returning here later doesn't reopen the drawer unexpectedly.
	useEffect(() => {
		return () => setIsMobileSidebarOpen(false);
	}, [setIsMobileSidebarOpen]);

	useEffect(() => {
		premiumNudgeDoneRef.current = false;
	}, []);

	useEffect(() => {
		if (id) posthog?.identify(id);
	}, [id, posthog]);

	// Lead-tracking: tell the iframe parent once the customer reaches quotes
	// they can actually see (status is complete). Fires at most once per mount;
	// no-op when not iframed or no parent origin was captured.
	useEffect(() => {
		if (quotesViewedNotifiedRef.current) return;
		if (!appData?.status || !statusConfig.groups.isComplete.includes(appData.status)) return;
		quotesViewedNotifiedRef.current = true;
		notifyParent('quotes_viewed');
	}, [appData?.status]);

	useEffect(() => {
		if (id && appData?.status && statusConfig.groups.isApplied.includes(appData.status))
			navigate(`/inbehandeling/${id}`);
	}, [id, appData?.status, navigate]);

	useEffect(() => {
		if (
			appData?.status &&
			['PENDING_QUOTE_REVIEW', 'EXIT_NO_SUPPORTED_POLICY_FOUND'].includes(appData.status)
		)
			navigate('/polis-niet-leesbaar');
	}, [appData?.status, navigate]);

	// Control modals based on status
	useEffect(() => {
		setKvkModalOpen(appData?.status === 'PENDING_CUSTOMER_COC_ENTRY');
	}, [appData?.status]);

	useEffect(() => {
		setSectorCodeModalOpen(appData?.status === 'PENDING_CUSTOMER_SECTOR_CODE_ENTRY');
	}, [appData?.status]);

	useEffect(() => {
		setWageInfoModalOpen(appData?.status === 'PENDING_CUSTOMER_WAGE_INFO_ENTRY');
	}, [appData?.status]);

	useEffect(() => {
		setSlstCoverageModalOpen(appData?.status === 'PENDING_CUSTOMER_SLST_COVERAGE_ENTRY');
	}, [appData?.status]);

	// Show contact details modal when processing quotes (only once per policy, persisted in localStorage).
	// Wait for the SLST coverage modal to close first to avoid stacking two modals.
	const isContactDialogEnabled = isFeatureActive(
		config,
		FeatureFlag.ENABLE_PROACTIVE_CONTACT_DIALOG,
		true
	);
	useEffect(() => {
		if (!id) return;
		if (slstCoverageModalOpen) return;
		if (!isContactDialogEnabled) return;

		const storageKey = getContactDetailsShownKey(id);
		const hasBeenShown = localStorage.getItem(storageKey) === 'true';

		if (appData?.status === 'PROCESSING_QUOTES' && !hasBeenShown) {
			localStorage.setItem(storageKey, 'true');
			setContactDetailsModalOpen(true);
		}
	}, [appData?.status, id, isContactDialogEnabled, slstCoverageModalOpen]);

	// Recommend entering renewal premium % when it is missing from the policy (once per visit while it stays missing)
	useEffect(() => {
		if (!id || !appData?.status) return;
		if (!statusConfig.groups.isComplete.includes(appData.status)) return;
		// No-policy flow: never proactively nudge, but don't force-close either —
		// the customer can still open the modal manually to enter their rate.
		if (appData.current_policy == null) {
			return;
		}

		if (currentPremiumRate) {
			premiumNudgeDoneRef.current = false;
			return;
		}

		if (premiumNudgeDoneRef.current) return;
		premiumNudgeDoneRef.current = true;
		setPremiumPercentageModalOpen(true);
	}, [id, appData?.status, appData?.current_policy, currentPremiumRate]);

	if (isError)
		return (
			<BasePage
				isError={isError}
				title={
					appData?.status
						? (statusConfig.descriptions as Record<string, string>)[appData.status]
						: undefined
				}
			/>
		);

	const isComplete = !!appData?.status && statusConfig.groups.isComplete.includes(appData.status);
	// Editing the current rate is available whenever the quote is complete,
	// including the no-policy flow where there is no policy to pull a rate from.
	// Editing from the mobile side panel should hand off to a modal, so close the
	// slide-over first (closing an already-closed panel on desktop is a no-op).
	const onEditPremiumPercentage = isComplete
		? () => {
				setIsMobileSidebarOpen(false);
				setPremiumPercentageModalOpen(true);
			}
		: undefined;
	const onEditSectorCode = isComplete
		? () => {
				setIsMobileSidebarOpen(false);
				setSectorCodeModalOpen(true);
			}
		: undefined;

	return (
		<>
			<section className="w-full xl:container xl:mx-auto px-6 xl:px-4 py-4 sm:py-6 xl:py-14">
				<div className="flex flex-wrap">
					<aside data-testid="sidebar" className="hidden xl:block xl:w-[25.7%] xl:pr-10 h-fit">
						<Sidebar
							onEditPremiumPercentage={onEditPremiumPercentage}
							onEditSectorCode={onEditSectorCode}
							handleTriggerAppStatePolling={restartPolling}
						/>
					</aside>
					{/* Below xl the sidebar collapses into a slide-over holding the same
					    "your details" panel, opened from the header hamburger. It covers
					    the full viewport height, including the fixed header, rather than
					    starting below it. */}
					<Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
						<SheetContent
							side="left"
							// Solid white now that the card is gone — it's the drawer's own
							// surface rather than a backdrop behind a white card.
							className="xl:hidden w-[88%] max-w-sm overflow-y-auto p-6 pt-9 bg-white"
							data-testid="mobile-sidebar"
						>
							<SheetTitle className="sr-only">{t('quotes.sidebar.title')}</SheetTitle>
							{/* No card here — the drawer itself is already the full-bleed
							    surface, so the content renders directly on it instead of
							    nesting the desktop card-in-a-card look. */}
							<div className="h-fit">
								<Sidebar
									onEditPremiumPercentage={onEditPremiumPercentage}
									onEditSectorCode={onEditSectorCode}
									handleTriggerAppStatePolling={restartPolling}
									showCard={false}
								/>
							</div>
						</SheetContent>
					</Sheet>
					<section data-testid="items" className="w-full xl:w-[74.3%]">
						<Items onRecalculate={restartPolling} />
					</section>
				</div>
			</section>

			<KvK open={kvkModalOpen} onOpenChange={setKvkModalOpen} onAfterSubmit={restartPolling} />
			<SectorCode
				open={sectorCodeModalOpen}
				onOpenChange={setSectorCodeModalOpen}
				onAfterSubmit={restartPolling}
			/>
			<WageInfo
				open={wageInfoModalOpen}
				onOpenChange={setWageInfoModalOpen}
				onAfterSubmit={restartPolling}
			/>
			<SlstCoverage open={slstCoverageModalOpen} onOpenChange={setSlstCoverageModalOpen} />
			<PremiumPercentage
				open={premiumPercentageModalOpen}
				onOpenChange={setPremiumPercentageModalOpen}
				onSuccess={restartPolling}
			/>
			<ContactDetailsDuringLoad
				open={contactDetailsModalOpen}
				onOpenChange={setContactDetailsModalOpen}
			/>
		</>
	);
};

export default Quote;
